import { Link } from '@remix-run/react'
import {
	motion,
	useScroll,
	useTransform,
	useSpring,
	useMotionValue,
} from 'framer-motion'
import { useEffect, useRef, useState } from 'react'

export function HeroSection() {
	const sectionRef = useRef<HTMLElement>(null)
	const { scrollY } = useScroll()
	const y = useTransform(scrollY, [0, 300], [0, 200])

	useEffect(() => {
		if (sectionRef.current) {
			const { height } = sectionRef.current.getBoundingClientRect()
			y.set(0)
			y.set(scrollY.get())
		}
	}, [y, scrollY])

	return (
		<motion.section
			ref={sectionRef}
			className="relative min-h-screen overflow-hidden bg-gradient-to-r from-purple-500 to-pink-500"
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			transition={{ duration: 0.5 }}
		>
			<div className="container relative z-10 mx-auto flex min-h-screen flex-col items-center justify-center px-4 text-center">
				<motion.h1
					className="font-pacifico mb-4 text-8xl font-bold sm:text-9xl md:text-[10rem] lg:text-[12rem]"
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 1, delay: 0.5 }}
					style={{
						textShadow:
							'0 0 20px rgba(255,255,255,0.7), 0 0 40px rgba(255,255,255,0.3)',
						WebkitTextStroke: '2px rgba(255,255,255,0.8)',
						color: 'transparent',
						backgroundImage: 'linear-gradient(to right, #f472b6, #a855f7)',
						WebkitBackgroundClip: 'text',
						backgroundClip: 'text',
					}}
				>
					LamaLoot
				</motion.h1>
				<motion.p
					className="mb-8 text-xl font-semibold text-white sm:text-2xl"
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 1, delay: 0.7 }}
				>
					Explore the Fortnite universe like never before
				</motion.p>
				<motion.button
					className="rounded-full bg-white px-8 py-3 text-lg font-semibold text-purple-600 shadow-lg transition-colors hover:bg-purple-100"
					whileHover={{ scale: 1.05 }}
					whileTap={{ scale: 0.95 }}
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 1, delay: 0.9 }}
				>
					<Link to="/cosmetics">Explore Skins</Link>
				</motion.button>
			</div>
			<motion.div
				className="absolute inset-0 z-0"
				initial={{ opacity: 0 }}
				animate={{ opacity: 0.2 }}
				transition={{ duration: 2 }}
			>
				<div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-pink-500 opacity-20" />
				<img
					src="/images/fortnite-background.jpg"
					alt="Fortnite Background"
					className="h-full w-full object-cover"
				/>
			</motion.div>
			<LiquidDivider y={y} />
		</motion.section>
	)
}

function LiquidDivider({ y }: { y: any }) {
	const [windowSize, setWindowSize] = useState({ width: 0, height: 0 })

	useEffect(() => {
		const updateSize = () => {
			setWindowSize({ width: window.innerWidth, height: window.innerHeight })
		}
		updateSize()
		window.addEventListener('resize', updateSize)
		return () => window.removeEventListener('resize', updateSize)
	}, [])

	return (
		<motion.div
			className="absolute bottom-0 left-0 w-full overflow-hidden"
			style={{ y }}
		>
			<svg
				className="relative block w-full"
				viewBox="0 0 1440 100"
				preserveAspectRatio="none"
			>
				<motion.path
					className="fill-current text-purple-500"
					d="M0,0 C240,80 480,20 720,40 C960,60 1200,80 1440,60 L1440,100 L0,100 Z"
					animate={{
						d: [
							'M0,0 C240,80 480,20 720,40 C960,60 1200,80 1440,60 L1440,100 L0,100 Z',
							'M0,0 C240,40 480,60 720,20 C960,40 1200,60 1440,80 L1440,100 L0,100 Z',
							'M0,0 C240,80 480,20 720,40 C960,60 1200,80 1440,60 L1440,100 L0,100 Z',
						],
					}}
					transition={{
						repeat: Infinity,
						duration: 10,
						ease: 'easeInOut',
					}}
				/>
				{windowSize.width > 0 && (
					<motion.path
						className="fill-current text-white opacity-30"
						d="M0,100 C240,100 480,100 720,100 C960,100 1200,100 1440,100 L1440,100 L0,100 Z"
						animate={{
							d: [
								'M0,100 C240,90 480,110 720,100 C960,90 1200,110 1440,100 L1440,100 L0,100 Z',
								'M0,100 C240,110 480,90 720,100 C960,110 1200,90 1440,100 L1440,100 L0,100 Z',
								'M0,100 C240,90 480,110 720,100 C960,90 1200,110 1440,100 L1440,100 L0,100 Z',
							],
						}}
						transition={{
							repeat: Infinity,
							duration: 5,
							ease: 'easeInOut',
						}}
					/>
				)}
			</svg>
		</motion.div>
	)
}
